import { useRef, useEffect, useCallback, DragEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentNugget, NUGGET_VARIANTS, PriorityPlaylist } from '@backrs_ui/common-ui';

import {
  handlePanelNuggetClick,
  startNuggetDrag,
} from '@backrs_ui/grow-ui/src/components/Nuggets/nugget.util';
import { getDraggableNuggetId, ResourceKey } from '@backrs_ui/grow-ui/src/models/models';
import { type HighlightedNugget } from '@backrs_ui/grow-ui/src/redux/boxData/boxData';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import { openSuggestedPlaylistViewStep } from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { NuggetCardItem } from './NuggetCardItem';

type DraggableNuggetCardProps = Readonly<{
  nugget: ContentNugget;
  resourceKey: ResourceKey;
  resourceId: string;
  highlightedNugget?: HighlightedNugget | null;
  fluid?: boolean;
}>;

export const DraggableNuggetCard = ({
  nugget,
  resourceKey,
  resourceId,
  highlightedNugget,
  fluid,
}: DraggableNuggetCardProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const nuggetRef = useRef<HTMLDivElement>(null);

  const isHighlighted =
    highlightedNugget?.resourceKey === resourceKey && highlightedNugget?.id === resourceId;

  useEffect(() => {
    if (isHighlighted && nuggetRef.current) {
      nuggetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [isHighlighted]);

  const handleDragStart = useCallback(
    (event: DragEvent<HTMLDivElement>) => {
      startNuggetDrag(event, {
        id: getDraggableNuggetId(nugget, resourceKey),
        type: resourceKey,
        title: nugget.title ?? resourceId,
      });
    },
    [resourceId, resourceKey, nugget],
  );

  const handleClick = useCallback(
    (clicked: ContentNugget) => {
      if (clicked.variant === NUGGET_VARIANTS.PLAYLIST && clicked.playlist_info) {
        dispatch(openSuggestedPlaylistViewStep(clicked.playlist_info as PriorityPlaylist));

        return;
      }

      handlePanelNuggetClick(clicked, navigate);
    },
    [navigate, dispatch],
  );

  return (
    <div
      id={`resource-nugget-${resourceId}`}
      ref={nuggetRef}
      draggable
      onDragStart={handleDragStart}
      style={{ cursor: 'grab' }}
    >
      <NuggetCardItem
        nugget={nugget}
        onClick={handleClick}
        isHighlighted={isHighlighted}
        fluid={fluid}
      />
    </div>
  );
};
